<template>
  <v-container>
    <v-list v-if="showLoader">
      <v-list-item v-for="idx in 6" :key="idx">
        <v-list-item-icon size="10">
          <v-skeleton-loader type="avatar"></v-skeleton-loader>
        </v-list-item-icon>

        <v-list-item-content>
          <v-skeleton-loader type="text"></v-skeleton-loader>
        </v-list-item-content>
      </v-list-item>
    </v-list>

    <ul class="timeline positionRelative" v-else>
      <li
        :class="{ seen: !item.seen }"
        class="my-5 pl-5"
        v-for="(item, index) in notificationList"
        :key="index"
        link
        @click="markSeen($event, item)"
        :data-test-id="`${dataTestId}-${item.id}`"
      >
        <v-list-item>
          <v-list-item-icon>
            <strong>{{ getDateAndMonth(item.timestamp) }}</strong>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title
              :inner-html.prop="formatMessage(item) | sanitize"
              :data-test-id="`${dataTestId}-${item.id}__notification-title`"
            ></v-list-item-title>
            <v-row no-gutters align="center" justify="space-between">
              <div>
                <v-list-item-subtitle class="py-2">{{
                  getHourAndMinute(item.timestamp)
                }}</v-list-item-subtitle>
              </div>
              <div>
                <v-row no-gutters align="center" v-if="item.priority === 101">
                  <v-tooltip left>
                    <template v-slot:activator="{ on }">
                      <a
                        v-on="on"
                        @click="
                          () =>
                            showProgressDetails(
                              item.extras &&
                                item.extras.links &&
                                item.extras.links.endPoint
                            )
                        "
                      >
                        <v-icon>mdi-chart-pie</v-icon>
                      </a>
                    </template>
                    <span class="white--text">Show progress</span>
                  </v-tooltip>
                </v-row>
              </div>
            </v-row>
          </v-list-item-content>
        </v-list-item>
      </li>
      <v-list-item v-if="canLoadMore">
        <v-list-item-icon size="10">
          <v-skeleton-loader type="avatar"></v-skeleton-loader>
        </v-list-item-icon>

        <v-list-item-content>
          <v-skeleton-loader type="text"></v-skeleton-loader>
        </v-list-item-content>
      </v-list-item>
      <div class="text-center" v-else><h6>All notification caught up!</h6></div>
    </ul>

    <x-dialog
      width="50%"
      :dialogTitle="progressData.label"
      :dialogState="progressDialogState"
      @toggleModal="val => (progressDialogState = val)"
    >
      <template slot="dialog_data">
        <v-row no-gutters align="center" class="chartWidgets">
          <div class="canvas-parent">
            <canvas
              v-if="Object.keys(progressChartData).length"
              width="380"
              height="380"
              :id="`pieCanvas-progress`"
              >Sorry, canvas not supported</canvas
            >
          </div>
          <div class="legend">
            <v-row
              align="center"
              v-for="(legend, index) in Object.keys(progressChartData)"
              class="ml-4 mb-2 legendWrapper font16"
              :key="index"
            >
              <span
                class="legendCircle bullet-point"
                :class="{
                  primaryColor: index === 0,
                  secondaryColor: index === 1,
                  tertiaryColor: index === 2
                }"
              ></span>
              <span
                >{{ legend }} {{ progressData.unit }} -
                {{ numberINString(progressData[legend.toLowerCase()]) }}</span
              >
            </v-row>
            <v-row align="center" class="ml-4 mb-2 legendWrapper font16">
              <span class="legendCircle bullet-point"></span>
              <span class="font-600"
                >Total {{ progressData.unit }} -
                {{ numberINString(progressData.total) }}</span
              >
            </v-row>
          </div>
        </v-row>
      </template>
    </x-dialog>
  </v-container>
</template>

<script>
import { mapActions, mapMutations, mapState } from 'vuex';
import diagJS from '@/assets/js/diag';
import colors from '@/constants/colors';
import debounce from 'lodash/debounce';
import { numberINString } from '@/helpers/common';
import config from '../../../config';
export default {
  props: {
    duration: {
      default: 1,
      type: Number,
      required: false
    },
    extraFilter: Object
  },
  watch: {
    notifsLoading() {
      this.notificationList = this.notificationDetails?.userNotifications || [];
    },
    // Getting notification data after filter apply
    extraFilter() {
      this.getNotifications();
    }
  },
  mounted() {
    this.initEvents();
    this.getNotifications();
  },
  beforeDestroy() {
    const timelineAnchors = document.querySelectorAll(
      '.timeline .v-list-item__title a'
    );
    timelineAnchors.forEach(element => {
      element.removeEventListener('click', () => {});
    });
  },
  data() {
    return {
      progressDialogState: false,
      pageNumber: 1,
      progressChartData: {},
      progressData: {},
      numberINString: numberINString,
      progressChartOptions: { title: 'On board' },
      notificationList: [],
      colorsArr: [
        colors.chartPrimary,
        colors.chartSecondary,
        colors.chartTertiary
      ],
      debounceInterval: 100,
      dataTestId:'notification-list-item'
    };
  },
  computed: {
    ...mapState({
      notificationDetails: state => state.notifications || {}
    }),
    notifsLoading() {
      return this.notificationDetails?.notifsLoading;
    },
    // return true, if more notifications are there to load in UI
    canLoadMore() {
      return this.notificationDetails?.canLoadMore;
    },
    showLoader() {
      return this.notifsLoading && !this.canLoadMore;
    }
  },
  methods: {
    ...mapMutations('notifications', [
      'setUnreadCount',
      'setFromLoadMore',
      'setNotifsLoading',
      'setUserNotifications',
      'updateNotificationsList'
    ]),
    ...mapActions('notifications', [
      'updateUnreadCount',
      'getNotificationsForDuration'
    ]),
    initEvents() {
      // assigning event handler for scroll event on notification list
      this.handleDebouncedScroll = debounce(
        this.handleScroll,
        this.debounceInterval
      );
      document.getElementsByClassName(
        'v-navigation-drawer__content'
      )[0].onscroll = this.handleDebouncedScroll;
    },
    /**
     * Function to mark notification as seen
     * @param {Object} event, event occured on click
     * @param {Object} notification, event occured on click
     */
    async markSeen(event, notification) {
      // only on click of circle
      if (event.target.nodeName.toUpperCase() === 'LI') {
        // return if already seen
        if (notification?.seen) {
          return;
        }
        try {
          await this.$services.notification.markAsSeen([notification.id]);
          // once marked seen, refresh notifs again
          this.getNotifications();
        } catch (err) {
          this.snackbar('error', err);
        }
      }
    },
    formatMessage(notif) {
      let message = notif.message;
      let links = notif?.extras?.links || {};

      const allLinkKeys = Object.keys(links);
      if (!allLinkKeys.length) {
        return message;
      }

      allLinkKeys.forEach(key => {
        let regexp = new RegExp(`\\[\\[(${key})\\]\\]`);
        message = message.replace(regexp, function(matched) {
          if (links[key]) {
            return links[key];
          }
          return matched;
        });
      });
      return message;
    },
    initPie(pieItems) {
      let keys = Object.keys(pieItems);

      let pieColors = {};
      for (let index = 0; index < keys.length; index++) {
        if (pieItems[keys[index]] !== 0) {
          const element = keys[index];
          pieColors[element] = this.colorsArr[index];
        }
      }

      let newObj = {};
      for (let key in pieItems) {
        if (pieItems.hasOwnProperty(key) && pieItems[key] !== 0) {
          newObj[key] = pieItems[key];
        }
      }

      this.$nextTick(() => {
        let canvas = document.getElementById('pieCanvas-progress');
        diagJS.PieChart(newObj, pieColors, canvas).draw();
      });
    },
    async getNotifications() {
      this.pageNumber = 1;
      this.notificationList = [];
      try {
        this.setUnreadCount(0);
        await this.setUserNotifications([]);
        await this.getNotificationsForDuration(
          this.getRequestObject(this.extraFilter.duration, this.extraFilter)
        );
        this.replaceTimelineRoutingEvents();
        // this.updateUnreadCount();
      } catch (err) {
        this.snackbar('error', err);
      }
    },
    /**
     * Function to make request object for payload of message call
     */
    getRequestObject(duration, extras = {}) {
      // calculation epoch for {x}(means duration) days before
      let starteDate = this.getUTCDateAsPerTimeAndDate()
        .add(parseInt(duration * -1), 'day')
        .startOf('day')
        .format('x');
      // calculation epoch for today
      let endDate = this.getUTCDateAsPerTimeAndDate().format('x');
      let obj = {
        from: +starteDate,
        order: 'DESC',
        pageNumber: this.pageNumber,
        pageSize: 10,
        sortBy: 'TIMESTAMP',
        to: +endDate
      };
      if (extras.showProgressOnly) {
        obj['priority'] = 101;
      }
      return obj;
    },
    /**
     * Function :- To get progress Data then open progress dialog
     * @param {String} endPoint
     */
    async showProgressDetails(endPoint) {
      try {
        if (!endPoint) {
          return this.snackbar('error', this.$message.ERROR);
        }
        const progressDetails = await this.$services.activeStatus.getActiveProcess(
          endPoint
        );
        const progress = this.parseResponse(progressDetails);
        if (progress) {
          this.progressData = progress;
          const processedValue = progress?.processed / progress?.total;
          const processed = isNaN(processedValue) ? 0 : processed;
          const failedValue = progress.failed / progress.total;
          const failed = isNaN(failedValue) ? 0 : failedValue;
          this.progressChartData = {
            processed,
            failed,
            processing: 1 - processed - failed
          };
          this.initPie(this.progressChartData);
          this.progressDialogState = true;
        }
      } catch (error) {
        this.snackbar('error', error);
      }
    },
    /** This function will be called whenever user scroll notification list
     * @param {Object} event, event occured on scroll
     */
    async handleScroll(event) {
      try {
        // element contains css class 'v-navigation-drawer__content'
        const notificationListWrapper = event?.srcElement;
        // Detecting if bottom of notification list arrived or not
        let isBottomArrived =
          Math.ceil(notificationListWrapper?.scrollTop) +
            notificationListWrapper?.offsetHeight >=
          notificationListWrapper?.scrollHeight;
        // if bottom of notification list arrived and canLodMore is true, then change page & make network call
        if (isBottomArrived && this.canLoadMore) {
          this.setFromLoadMore(true);
          this.pageNumber += 1;
          const requestObj = this.getRequestObject(
            this.extraFilter?.duration,
            this.extraFilter
          );
          await this.getNotificationsForDuration(requestObj);
          this.replaceTimelineRoutingEvents();
          this.setFromLoadMore(false);
        }
      } catch (error) {
        this.setFromLoadMore(false);
      }
    },
    replaceTimelineRoutingEvents() {
      const timelines = document.querySelectorAll(
        '.timeline .v-list-item__title a'
      );
      const self = this;
      timelines.forEach(timelineElement => {
        const actualURL = timelineElement.href;
        const formattedURL = actualURL.replace(config.baseUrl, '');
        timelineElement.removeEventListener('click', () => {});
        timelineElement.addEventListener('click', event => {
          event.preventDefault();
          event.stopPropagation();
          if (location.pathname !== formattedURL) {
            self.$router.push(formattedURL);
          }
        });
      });
    }
  }
};
</script>
<style scoped>
.v-list-item__icon {
  margin-top: 0;
}
.v-list-item__content {
  padding-top: 0;
}
.v-list-item__title {
  white-space: normal;
}
.canvas-parent {
  width: 380px;
  height: 380px;
}
ul.timeline {
  list-style-type: none;
}
ul.timeline:before {
  content: ' ';
  background: #dedfe0;
  display: inline-block;
  position: absolute;
  left: 29px;
  width: 2px;
  height: 100%;
  z-index: 400;
}
ul.timeline > li:before {
  background: white;
  border: 3px solid #58e2c2;
  border-radius: 50%;
  content: ' ';
  cursor: pointer;
  display: inline-block;
  height: 20px;
  left: 20px;
  position: absolute;
  width: 20px;
  z-index: 400;
}

.v-list-item:hover {
  opacity: 1;
}
.seen:before {
  background: #58e2c2 !important;
  border: 3px double white !important;
}
</style>
